import type { CreateReportFormData, Report } from "@/react/types/Live/Report";
import type {
  Participant,
  ParticipantRole,
} from "@circle-react/types/Live/Participant";
import type { Recorder } from "@circle-react/types/Live/Recorder";
import type { FetchOptions } from "../helpers/backendRequestHelpers";
import {
  fetchRequest,
  reactQueryDelete,
  reactQueryPatch,
  reactQueryPost,
  reactQueryPut,
} from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";
import type { Cohost } from "../types/Live/Cohost";
import { QAQuestionsApi } from "./QAQuestionsApi";
import { QARepliesApi } from "./QARepliesApi";
import { participantsApi } from "./participantsApi";
import { spotlightApi } from "./spotlightApi";

interface CreateParams extends FetchOptions {
  formData: Record<string, unknown>;
}

const create = ({ formData, ...fetchRequestOptions }: CreateParams) =>
  fetchRequest<unknown>(internalApi.liveStreams.create(), {
    method: "POST",
    body: JSON.stringify(formData),
    ...fetchRequestOptions,
  });

interface UpdateParams {
  formData: Record<string, unknown>;
  id?: string | number;
}

const update = ({ id, formData }: UpdateParams) =>
  reactQueryPut<unknown, Record<string, unknown>>(
    internalApi.liveStreams.update({ id }),
    formData,
  );

interface GoLiveParams extends FetchOptions {
  id?: string | number;
}

const goLive = ({ id, ...fetchRequestOptions }: GoLiveParams) =>
  fetchRequest<unknown>(internalApi.liveStreams.goLive({ id }), {
    method: "PUT",
    ...fetchRequestOptions,
  });

interface ChatTranscriptParams extends FetchOptions {
  id?: string | number;
}

const chatTranscript = ({ id, ...fetchRequestOptions }: ChatTranscriptParams) =>
  fetchRequest<unknown>(internalApi.liveStreams.chatTranscript({ id }), {
    method: "GET",
    ...fetchRequestOptions,
  });

interface JoinParams extends FetchOptions {
  id?: string | number;
  role: string;
}

const join = ({ id, role, ...fetchRequestOptions }: JoinParams) =>
  fetchRequest<Participant>(internalApi.liveStreams.join({ id }), {
    method: "POST",
    body: JSON.stringify({ role }),
    ...fetchRequestOptions,
  });

interface LeaveParams extends FetchOptions {
  id?: string | number;
  participantId?: string | number;
  reason?: string;
}

const leave = ({ id, participantId, reason }: LeaveParams) =>
  reactQueryDelete<Participant, { reason?: string }>(
    internalApi.liveStreams.leave({ id, participantId }),
    { reason },
  );

export interface AddCohostsPayload {
  cohosts_community_member_ids: number[];
}

interface AddCohostsParams {
  formData: AddCohostsPayload;
  roomId?: string | number;
}

const addCohosts = ({ roomId, formData }: AddCohostsParams) =>
  reactQueryPost<Cohost[], AddCohostsPayload>(
    internalApi.liveStreams.addCohosts({ roomId }),
    formData,
  );

interface AddModeratorParams extends FetchOptions {
  formData: Record<string, unknown>;
  roomId?: string | number;
}

const addModerator = ({
  roomId,
  formData,
  ...fetchRequestOptions
}: AddModeratorParams) =>
  fetchRequest<unknown>(internalApi.liveStreams.addModerator({ roomId }), {
    method: "POST",
    body: JSON.stringify(formData),
    ...fetchRequestOptions,
  });

interface RemoveCohostParams extends FetchOptions {
  id?: string | number;
  roomId?: string | number;
}

const removeCohost = ({
  id,
  roomId,
  ...fetchRequestOptions
}: RemoveCohostParams) =>
  fetchRequest<unknown>(internalApi.liveStreams.removeCohost({ id, roomId }), {
    method: "DELETE",
    ...fetchRequestOptions,
  });

interface ChangeRoleParams {
  participantId?: string | number;
  role: ParticipantRole;
  roomId?: string | number;
}

interface ChangeRolePayload {
  role: ParticipantRole;
}

const changeRole = ({ roomId, participantId, role }: ChangeRoleParams) =>
  reactQueryPatch<Participant, ChangeRolePayload>(
    internalApi.liveStreams.changeRole({ roomId, participantId }),
    { role },
  );

interface EndStreamParams extends FetchOptions {
  id?: string | number;
}

const endStream = ({ id, ...fetchRequestOptions }: EndStreamParams) =>
  fetchRequest<unknown>(internalApi.liveStreams.endStream({ id }), {
    method: "DELETE",
    ...fetchRequestOptions,
  });

interface EndStreamDelayedParams extends FetchOptions {
  id?: string | number;
}

const endStreamDelayed = ({
  id,
  ...fetchRequestOptions
}: EndStreamDelayedParams) =>
  fetchRequest<unknown>(internalApi.liveStreams.endStreamDelayed({ id }), {
    method: "DELETE",
    ...fetchRequestOptions,
  });

interface LeaveFeedbackParams extends FetchOptions {
  formData: {
    score: number;
    message: string;
    device_info: string;
  };
  id?: string | number;
  participantId?: string | number;
}

const leaveFeedback = ({
  id,
  participantId,
  formData,
  ...fetchRequestOptions
}: LeaveFeedbackParams) =>
  fetchRequest<unknown>(
    internalApi.liveStreams.leaveFeedback({ id, participantId }),
    {
      method: "POST",
      body: JSON.stringify(formData),
      ...fetchRequestOptions,
    },
  );

interface CreateRecorderParams {
  id?: string | number;
  recording_key: string | number;
}

interface CreateRecorderPayload {
  recording_key: string | number;
}

const createRecorder = ({ id, recording_key }: CreateRecorderParams) =>
  reactQueryPost<Recorder, CreateRecorderPayload>(
    internalApi.liveStreams.createRecorder({ id }),
    {
      recording_key,
    },
  );

interface DeleteRoomRecordingParams {
  id?: string | number;
  roomId?: string | number;
}

const deleteRoomRecording = ({ roomId, id }: DeleteRoomRecordingParams) =>
  reactQueryDelete<unknown, object>(
    internalApi.liveStreams.deleteRoomRecording({ roomId, id }),
  );

interface CreateLiveReportParams {
  formData: CreateReportFormData;
  roomId?: string | number;
}

const createLiveReport = ({ roomId, formData }: CreateLiveReportParams) =>
  reactQueryPost<Report, CreateReportFormData>(
    internalApi.liveStreams.createLiveReport({ roomId }),
    formData,
  );

export const liveStreamApi = {
  create,
  update,
  goLive,
  chatTranscript,
  join,
  leave,
  addCohosts,
  removeCohost,
  changeRole,
  endStream,
  endStreamDelayed,
  leaveFeedback,
  createRecorder,
  deleteRoomRecording,
  createLiveReport,
  addModerator,
  ...QAQuestionsApi,
  ...QARepliesApi,
  ...spotlightApi,
  ...participantsApi,
};
